/**
 * Founder Profile — official BSGOTECH founder card.
 *
 * Used in:
 *   - /leadership dedicated page
 *   - Homepage `About` section (compact variant)
 *   - Any future corporate presentation surface
 *
 * NEVER used as a hero banner or decorative background. The founder supports
 * the company's credibility — the corporate identity remains the primary visual.
 *
 * Fully DE / EN / PT via shared LangCtx.
 */
import React from "react";
import { motion } from "framer-motion";
import { MapPin, ExternalLink } from "lucide-react";
import { LangCtx } from "@/i18n/neunexus";

const P = {
  bg:      "#080B12",
  bg2:     "#0A0F1A",
  glass:   "rgba(255,255,255,0.045)",
  border:  "rgba(255,255,255,0.08)",
  ink:     "#E8ECF0",
  dim:     "rgba(232,236,240,0.68)",
  silver:  "#C0C7CE",
  green:   "#22C55E",
  blue:    "#3B82F6",
  teal:    "#14B8A6",
  rose:    "#F43F5E",
};

const DATA = {
  en: {
    eyebrow: "Leadership · Founder",
    name: "Cleiton Barreto dos Santos",
    title: "Founder & CEO",
    subtitle: "BSGOTECH",
    location: "Germany · Europe",
    bio: "Cleiton Barreto dos Santos is the Founder and CEO of BSGOTECH. He leads the company's long-term vision, focused on building intelligent digital ecosystems through technology integration, artificial intelligence and strategic collaboration. Under his leadership, BSGOTECH develops the NeuNexus integration platform together with specialized solutions such as RENALIX and NeuGreen Energy.",
    tags: ["Founder", "Germany", "Technology Strategy", "Artificial Intelligence", "Industrial Innovation", "Digital Ecosystems", "Renewable Energy", "Healthcare Innovation"],
    quote: "We don't build every technology. We build the ecosystem that connects them.",
    imageAlt: "Cleiton Barreto dos Santos — Founder of BSGOTECH",
  },
  de: {
    eyebrow: "Leadership · Gründer",
    name: "Cleiton Barreto dos Santos",
    title: "Gründer & CEO",
    subtitle: "BSGOTECH",
    location: "Deutschland · Europa",
    bio: "Cleiton Barreto dos Santos ist Gründer und CEO der BSGOTECH. Er verantwortet die langfristige Unternehmensstrategie mit dem Ziel, intelligente digitale Ökosysteme durch Technologieintegration, Künstliche Intelligenz und strategische Zusammenarbeit aufzubauen. Unter seiner Leitung entwickelt BSGOTECH die Integrationsplattform NeuNexus sowie spezialisierte Lösungen wie RENALIX und NeuGreen Energy.",
    tags: ["Gründer", "Deutschland", "Technologiestrategie", "Künstliche Intelligenz", "Industrielle Innovation", "Digitale Ökosysteme", "Erneuerbare Energie", "Gesundheitsinnovation"],
    quote: "Wir bauen nicht jede Technologie. Wir bauen das Ökosystem, das sie verbindet.",
    imageAlt: "Cleiton Barreto dos Santos — Gründer von BSGOTECH",
  },
  pt: {
    eyebrow: "Liderança · Fundador",
    name: "Cleiton Barreto dos Santos",
    title: "Fundador & CEO",
    subtitle: "BSGOTECH",
    location: "Alemanha · Europa",
    bio: "Cleiton Barreto dos Santos é o Fundador e CEO da BSGOTECH. Lidera a visão estratégica da empresa, focada no desenvolvimento de ecossistemas digitais inteligentes através da integração de tecnologias, inteligência artificial e colaboração estratégica. Sob a sua liderança, a BSGOTECH desenvolve a plataforma de integração NeuNexus e soluções especializadas como a RENALIX e a NeuGreen Energy.",
    tags: ["Fundador", "Alemanha", "Estratégia Tecnológica", "Inteligência Artificial", "Inovação Industrial", "Ecossistemas Digitais", "Energia Renovável", "Inovação em Saúde"],
    quote: "Não construímos toda a tecnologia. Construímos o ecossistema que as liga.",
    imageAlt: "Cleiton Barreto dos Santos — Fundador da BSGOTECH",
  },
};

const useF = () => {
  const { lang } = React.useContext(LangCtx);
  return DATA[lang] || DATA.en;
};

/**
 * FounderCard — the reusable executive card.
 * Variants:
 *   - "full":    dedicated /leadership page — larger portrait, tags grid, quote block.
 *   - "compact": embedded in the homepage About section — smaller portrait, no quote.
 */
export default function FounderCard({ variant = "full" }) {
  const t = useF();
  const isFull = variant === "full";

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      data-testid="founder-card"
      className="relative rounded-2xl overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${P.blue}0e 0%, ${P.bg2} 55%, ${P.green}0a 100%)`,
        border: `1px solid ${P.border}`,
        backdropFilter: "blur(18px)",
      }}
    >
      {/* subtle atmospheric accents */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
        backgroundImage: `radial-gradient(circle at 15% 20%, ${P.blue}22 0%, transparent 55%), radial-gradient(circle at 85% 85%, ${P.green}18 0%, transparent 55%)`,
      }} />

      <div className={`relative grid ${isFull ? "lg:grid-cols-[minmax(260px,320px)_1fr]" : "sm:grid-cols-[160px_1fr]"} gap-6 lg:gap-10 p-6 lg:p-10 items-center`}>
        {/* Portrait */}
        <div className="relative">
          <div className="absolute inset-0 rounded-xl pointer-events-none" aria-hidden="true" style={{
            background: `radial-gradient(circle at 50% 50%, ${P.blue}33 0%, transparent 65%)`,
            filter: "blur(20px)",
          }} />
          <div className="relative rounded-xl overflow-hidden"
            style={{
              background: `linear-gradient(180deg, ${P.bg2} 0%, ${P.bg} 100%)`,
              border: `1px solid ${P.border}`,
              boxShadow: `0 20px 60px -20px rgba(0,0,0,0.55)`,
              aspectRatio: "3/4",
            }}
          >
            <img
              src="/founder/portrait.png"
              alt={t.imageAlt}
              className="w-full h-full object-cover object-top"
              loading="lazy"
              data-testid="founder-portrait"
            />
          </div>
        </div>

        {/* Information */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.green }}>{t.eyebrow}</div>
          <h3 className="mt-3 font-semibold tracking-tight" style={{ color: "#fff", fontSize: isFull ? 32 : 22, letterSpacing: "-0.015em" }}>
            {t.name}
          </h3>
          <div className="mt-2 text-[13.5px]" style={{ color: P.silver }}>
            {t.title} · <span style={{ color: "#fff" }}>{t.subtitle}</span>
          </div>
          <div className="mt-1 inline-flex items-center gap-1.5 text-[11.5px]" style={{ color: P.dim }}>
            <MapPin size={11} /> {t.location}
          </div>

          <p className={`mt-5 leading-relaxed ${isFull ? "text-[13.5px]" : "text-[12.5px]"}`} style={{ color: P.ink, opacity: 0.9 }}>
            {t.bio}
          </p>

          {/* Tags */}
          <div className="mt-6 flex flex-wrap gap-1.5" data-testid="founder-tags">
            {t.tags.map((tag, i) => (
              <span key={i}
                className="text-[10.5px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-md"
                style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${P.border}`, color: P.silver }}>
                {tag}
              </span>
            ))}
          </div>

          {isFull && (
            <blockquote className="mt-8 pl-5 italic text-[14px] leading-relaxed"
              style={{ color: P.ink, borderLeft: `2px solid ${P.green}66` }}>
              &ldquo;{t.quote}&rdquo;
            </blockquote>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export { DATA as FOUNDER_DATA };
